import { SearchOutlined } from "@ant-design/icons";
import { Button, Flex, Input, Select } from "antd";
import React, { useEffect, useState } from "react";
import { Route, Switch } from "react-router-dom/cjs/react-router-dom.min.js";
import { ImportButton } from "~/components/buttons/ImportButton.jsx";
import { BreadcrumbHeader } from "~/components/layout/breadcrumbHeader.jsx";
import { $t } from "~/i18n.js";
import { api } from "~/lib/api.js";
import { selectElementByID } from "~/lib/helpers.js";
import { AdminFlightColumns } from "./components/AdminFlightColumns.jsx";
import { AdminFlightEditModal } from "./components/AdminFlightEditModal.jsx";
import { ImportDialogFlights } from "./components/ImportDialogFlights.jsx";
import { AdminFlightTable } from "./components/AdminFlightTable.jsx";
import { useSelector } from "react-redux";
import { AdminFlightChangesTable } from "./components/AdminFlightChangesTable.jsx";
import { Tabs } from "antd";

export function AdminFlights({ history, match }) {

  const tab = match?.params?.tab || "flights";
  
  const { profile } = useSelector((state) => state.persist);

  const [showImportModal, setShowImportModal] = useState(false);

  const [changes, setChanges] = useState([]);
  const [flights, setFlights] = useState([]);
  const updateFlight = (newFlight) => {
    let newFlights = flights.map((u) => {
      if (u.id == newFlight.id) return newFlight;
      return u;
    });
    setFlights(newFlights);

    setChanges(changes.map((c) => {
      if (c.uuid == newFlight.uuid) return {...c, ...newFlight};
      return c;
    }));
  };

  const [currentPageNumber, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    nextPage: null,
    prevPage: null,
    numberFlights: null,
  });

  const [query, setQuery] = useState([]);

  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const years = [];
  for (let year = currentYear; year >= 1990; year--) years.push(year);

  const constructFlightURL = (endpoint) => `${endpoint}?query=${query}&year=${year}`
  const onExportClick = (format = "csv") => api.openFileGET(`${constructFlightURL("admin/flight_export.json")}&format=${format}`);

  const [hasUpdated, setHasUpdated] = useState(false);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (tab === 'changes') {
      getChanges();
    } else {
      getFlights();
    }
  }, [tab]);

  const getFlights = async (newPage = null) => {
    let page = newPage || currentPageNumber;
    setLoading(true);
    const data = await api.get(
      `${constructFlightURL("admin/flights.json")}&page=${page}`
    );
    if (data) {
      setFlights(data.results);
      setPagination({
        nextPage: data.next,
        prevPage: data.previous,
        numberFlights: data.count,
      });
      setHasUpdated(false);
    }
    setLoading(false);
  };

  const getChanges = async (newPage = null) => {
    let page = newPage || currentPageNumber;
    setLoading(true);
    const data = await api.get(
      `${constructFlightURL("admin/flight_changes.json")}&page=${page}`
    );
    if (data) {
      setChanges(data.results.map(c => ({change_id: c.id, change_date: c.date_created, ...c, ...c.flight})));
      setPagination({
        nextPage: data.next,
        prevPage: data.previous,
        numberFlights: data.count,
      });
      setHasUpdated(false);
    }
    setLoading(false);
  };
  
  const renderedFlightForm = ({ history, match }) => {
    let flight = selectElementByID(tab === 'changes' ? changes : flights, "uuid", match.params.uuid);
    console.log('renderedFlightForm', flight, match.params.uuid, tab);
    return (
      <AdminFlightEditModal
        flight={flight}
        onClose={() => history.replace(`/admin/flights/${tab}`)}
        updateFlight={updateFlight}
      />
    );
  };

  return (
    <Flex gap="middle" vertical>
      <Switch>
        {(flights.length || changes.length) && (
          <Route
            path="/admin/flights/:tab/:uuid"
            component={renderedFlightForm}
          />
        )}
      </Switch>

      <Flex align="center" justify="space-between">
        <BreadcrumbHeader breadcrumbs={[$t("Flights")]} />

        {profile.wijzig_leden && (
          <ImportButton onClick={() => setShowImportModal(true)} />
        )}

        {profile.wijzig_leden && (
          <ImportDialogFlights
            refreshAfterImport={getFlights}
            isModal={showImportModal}
            closeModal={() => setShowImportModal(false)}
          />
        )}
      </Flex>

      <Flex gap={12}>
        <Input.Search
          style={{ maxWidth: 250 }}
          value={query}
          allowClear
          size="small"
          placeholder={$t("Zoek")}
          onChange={(event) => {
            setHasUpdated(true);
            setQuery(event.target.value);
          }}
        />

        <Select
          showSearch
          style={{ width: 200 }}
          size="small"
          value={year}
          filterOption={(input, option) => option?.key.includes(input)}
          onChange={(year) => {
            setHasUpdated(true);
            setYear(year);
          }}
        >
          {years.map((year) => (
            <Select.Option key={year} value={year}>
              {year}
            </Select.Option>
          ))}
        </Select>

        <Button
          type="primary"
          disabled={!hasUpdated}
          size="small"
          onClick={(e) => {
            e.preventDefault();
            if (tab === "changes") {
              getChanges();
            } else {
              getFlights();
          }}}
          icon={<SearchOutlined />}
        >
          {$t("Zoek")}
        </Button>
      </Flex>

      <Tabs
        activeKey={tab}
        onChange={(activeKey) => {
          history.push(`/admin/flights/${activeKey}`);
        }}
        items={[
          {
            key: "flights",
            label: $t("Vluchten"),
            children: (
              <AdminFlightTable
                flights={flights}
                loading={loading}
                onRowClick={(flight) =>
                  history.push(`/admin/flights/flights/${flight.uuid}`)
                }
                pagination={pagination}
                currentPageNumber={currentPageNumber}
                setCurrentPage={setCurrentPage}
                getFlights={getFlights}
                setFlights={setFlights}
                onExportClick={onExportClick}
              />
            ),
          },
          {
            key: "changes",
            label: $t("Wijzigingen"),
            children: (
              <AdminFlightChangesTable
                changes={changes}
                getChanges={getChanges}
                loading={loading}
                onRowClick={(flight) =>
                  history.push(`/admin/flights/changes/${flight.uuid}`)
                }
                pagination={pagination}
                currentPageNumber={currentPageNumber}
                setCurrentPage={setCurrentPage}
              />
            ),
          },
        ]}
      />
    </Flex>
  );
}
